import {
  ActionIcon,
  ComboboxItem,
  Group,
  MultiSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconPlus, IconTrash } from "@tabler/icons-react"
import { FieldConstraint } from "@tfg/db"
import {
  AutoCompleteWithQuery,
  Button,
  LabelWithTooltip,
  Select,
} from "@tfg/ui"
import { TFunction } from "i18next"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { forwardRef } from "react"
import { useTranslation } from "react-i18next"
import slugify from "slugify"
import z from "zod"

import { AdminRequests, AdminResponses } from "@tfg/types/endpoints/admin"
import {
  CustomFieldDocumentRequirement,
  CustomFieldValueRequirement,
} from "@tfg/types/endpoints/traceability"
import {
  CustomFieldDefinitionCreateSchema,
  CustomFieldDefinitionValidationType,
} from "@tfg/types/schemas/customField"
import { GetEcobalyseFieldsResponseDto } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/get-ecobalyse-fields.response.dto"
import { PaginationType } from "@tfg/types/schemas/utils"

import { queryClient } from "~/backend/queryClient.ts"
import { useActivitiesContext } from "~/components/contexts/useActivitiesContext"
import { useOrganizationsContext } from "~/pages/Admin/MasterLists/CustomFields/contexts/useOrganizationsContext"
import { useGetEcobalyseFieldsQuery } from "~/pages/Admin/MasterLists/CustomFields/hooks/useGetEcobalyseFieldsQuery"

import {
  useCreateCustomFieldMutation,
  useCreateTextContentMutation,
  useListTextContentsQuery,
  useUpdateCustomFieldMutation,
} from "./hooks"

interface Props {
  customField?: CustomFieldDefinitionValidationType
  close: () => void
}

export const CustomFieldsForm = ({ customField, close }: Props) => {
  const { t } = useTranslation("admin")
  const activities = useActivitiesContext()
  const onSuccess = () => {
    form.reset()
    close()
  }

  const organizations = useOrganizationsContext()

  const { mutate: createCustomFieldDefinition, isPending: isCreating } =
    useCreateCustomFieldMutation({
      onSuccess,
    })

  const { mutate: updateTextContent, isPending: isLoadingTextContent } =
    useCreateTextContentMutation({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["textContents"],
        })
        form.setFieldValue("title", data.originalText)
      },
    })

  const { mutate: updateCustomFieldDefinition, isPending: isUpdating } =
    useUpdateCustomFieldMutation(
      { id: customField?.id },
      {
        onSuccess,
      }
    )
  const isLoading = isCreating || isUpdating

  const { data: ecobalyseFields, isPending: isLoadingEcobalyseFields } =
    useGetEcobalyseFieldsQuery()

  const form = useForm<AdminRequests.CustomFieldDefinitionCreate>({
    validate: zod4Resolver(CustomFieldDefinitionCreateSchema),
    initialValues: {
      forActivityId: customField?.forActivityId ?? "",
      textContent: customField?.textContent ?? {
        originalText: "",
        TextTranslation: [],
      },
      onActivityId: customField?.onActivityId ?? null,
      enabledForOrganizationsIds: customField?.enabledForOrganizationsIds ?? [],
      constraint: customField?.constraint ?? "STRING",
      enumValuesOriginalText: customField?.enumValuesOriginalText ?? [],
      csvColumnName: customField?.csvColumnName ?? "",
      message: customField?.message ?? null,
      documentRequirement:
        customField?.documentRequirement ?? CustomFieldDocumentRequirement.NONE,
      fieldRequirement:
        customField?.fieldRequirement ?? CustomFieldValueRequirement.OPTIONAL,
      min: customField?.min ?? null,
      max: customField?.max ?? null,
      ecobalyseBalyseFieldId: customField?.ecobalyseBalyseFieldId ?? null,
    },
  })

  const handleSubmit = () => {
    if (customField) {
      updateCustomFieldDefinition(form.values)
    } else {
      createCustomFieldDefinition(form.values)
    }
  }
  const parser = CustomFieldDefinitionCreateSchema.safeParse(form.values)

  const errors = parser.success ? undefined : z.flattenError(parser.error)

  return (
    <>
      <form onSubmit={form.onSubmit(() => handleSubmit())}>
        <Stack>
          <MultiSelect
            data={organizations.map((organization) => ({
              value: organization.id.toString(),
              label: organization.name,
            }))}
            searchable
            label={
              <LabelWithTooltip
                withAsterisk
                label={t("customFields.labels.organization")}
                hoverHelperText={t("customFields.tooltips.organization")}
              />
            }
            placeholder={t("customFields.placeholders.organization")}
            value={
              form.values.enabledForOrganizationsIds?.map((organizationId) =>
                organizationId.toString()
              ) ?? []
            }
            onChange={(value) => {
              form.setValues({
                ...form.values,
                enabledForOrganizationsIds: value,
              })
            }}
          />
          <Select
            searchable
            data={activities.map((activity) => ({
              value: activity.id,
              label: activity.name,
            }))}
            label={
              <LabelWithTooltip
                withAsterisk
                label={t("customFields.labels.forActivity")}
                hoverHelperText={t("customFields.tooltips.forActivity")}
                justify="space-between"
              />
            }
            placeholder={t("customFields.placeholders.forActivity")}
            {...form.getInputProps("forActivityId")}
          />

          <AutoCompleteWithQuery<AdminResponses.TextContent, PaginationType>
            label={
              <LabelWithTooltip
                label={t("customFields.labels.orginalText")}
                hoverHelperText={t("customFields.tooltips.orginalText")}
                justify="space-between"
                withAsterisk
              />
            }
            placeholder={t("customFields.placeholders.orginalText")}
            query={useListTextContentsQuery}
            parseOptionFunction={(data) =>
              data?.map((item) => ({
                label: item.originalText,
                value: item.originalText,
              })) || []
            }
            isLoading={isLoadingTextContent}
            onCreate={(query) => {
              updateTextContent({ originalText: query })
              form.setValues({
                ...form.values,
                textContent: {
                  originalText: query,
                  TextTranslation: [],
                },
                csvColumnName: slugify(query, { lower: true }),
              })
              return null
            }}
            onChange={(value) => {
              if (value) {
                form.setValues({
                  ...form.values,
                  textContent: {
                    originalText: value,
                    TextTranslation: [],
                  },
                  csvColumnName: slugify(value, { lower: true }),
                })
              } else {
                form.setValues({
                  ...form.values,
                  textContent: undefined,
                  csvColumnName: undefined,
                })
              }
            }}
            value={form.values.textContent?.originalText}
            error={
              errors?.fieldErrors?.textContent
                ? errors.fieldErrors.textContent[0]
                : undefined
            }
          />

          <TextInput
            disabled
            label={
              <LabelWithTooltip
                label={t("customFields.labels.csvColumnName")}
                hoverHelperText={t("customFields.tooltips.csvColumnName")}
                justify="space-between"
                withAsterisk
              />
            }
            placeholder={t("customFields.placeholders.csvColumnName")}
            value={
              form.values.textContent?.originalText
                ? slugify(form.values.textContent?.originalText, {
                    lower: true,
                  })
                : ""
            }
          />
          <Select
            data={Object.values(FieldConstraint).map((value) => ({
              value,
              label: customFieldConstaintTranslations(value, t),
            }))}
            label={
              <LabelWithTooltip
                label={t("customFields.labels.constraint")}
                hoverHelperText={t("customFields.tooltips.constraint")}
                justify="space-between"
                withAsterisk
              />
            }
            placeholder={t("customFields.placeholders.constraint")}
            value={form.values.constraint}
            onChange={(value) => {
              form.getInputProps("constraint").onChange(value)
              if (
                form.values.constraint !== "SELECT" &&
                form.values.constraint !== "MULTI_SELECT"
              ) {
                form.setFieldValue("enumValuesOriginalText", [])
              }
              form.validateField("constraint")
            }}
            error={
              errors?.fieldErrors?.constraint
                ? errors.fieldErrors.constraint[0]
                : undefined
            }
          />

          {(form.values.constraint === "SELECT" ||
            form.values.constraint === "MULTI_SELECT") && (
            <Stack>
              <Title order={5}>{t("customFields.enum_values")}</Title>
              <Group>
                {form.values.enumValuesOriginalText.map(
                  (
                    value: CustomFieldDefinitionValidationType["enumValuesOriginalText"][number],
                    index: number
                  ) => (
                    <Group key={index}>
                      <TextInput
                        value={value.originalText}
                        onChange={(event) => {
                          const newEnumValues = [
                            ...form.values.enumValuesOriginalText,
                          ]
                          newEnumValues[index] = {
                            ...newEnumValues[index],
                            originalText: event.currentTarget.value,
                          }
                          form.setFieldValue(
                            "enumValuesOriginalText",
                            newEnumValues
                          )
                        }}
                        rightSection={
                          <ActionIcon
                            onClick={() => {
                              const newEnumValues = [
                                ...form.values.enumValuesOriginalText,
                              ]
                              newEnumValues.splice(index, 1)
                              form.setFieldValue(
                                "enumValuesOriginalText",
                                newEnumValues
                              )
                            }}
                          >
                            <IconTrash />
                          </ActionIcon>
                        }
                      />
                      {areCustomFieldEcobalyseFieldValuesRequired(
                        form.values.ecobalyseBalyseFieldId,
                        ecobalyseFields
                      ) && (
                        <Select
                          data={ecobalyseFields
                            ?.find(
                              (field) =>
                                field.id === form.values.ecobalyseBalyseFieldId
                            )
                            ?.fieldValues.map((fieldValue) => ({
                              value: fieldValue.id.toString(),
                              label: fieldValue.value,
                            }))}
                          value={value.ecobalyseBlayseFieldValueToTextContentId?.toString()}
                          label={
                            <LabelWithTooltip
                              label={t(
                                "customFields.labels.ecobalyseFieldValue"
                              )}
                              hoverHelperText={t(
                                "customFields.tooltips.ecobalyseFieldValue"
                              )}
                              justify="space-between"
                            />
                          }
                          placeholder={t(
                            "customFields.placeholders.ecobalyseFieldValue"
                          )}
                          onChange={(newValue) => {
                            const newEnumValues = [
                              ...form.values.enumValuesOriginalText,
                            ]
                            newEnumValues[index] = {
                              ...newEnumValues[index],
                              ecobalyseBlayseFieldValueToTextContentId:
                                newValue,
                            }
                            form.setFieldValue(
                              "enumValuesOriginalText",
                              newEnumValues
                            )
                          }}
                        />
                      )}
                    </Group>
                  )
                )}
              </Group>
              <Button
                icon={<IconPlus />}
                w={200}
                onClick={() => {
                  form.setValues({
                    ...form.values,
                    enumValuesOriginalText: [
                      ...form.values.enumValuesOriginalText,
                      {
                        originalText: "",
                        ecobalyseBlayseFieldValueToTextContentId: null,
                      },
                    ],
                  })
                }}
              >
                {t("customFields.add_enum_value")}
              </Button>
            </Stack>
          )}

          {form.values.constraint === "NUMBER" && (
            <Group>
              <TextInput
                label={
                  <LabelWithTooltip
                    label={t("customFields.labels.min")}
                    hoverHelperText={t("customFields.tooltips.min")}
                    justify="space-between"
                  />
                }
                placeholder={t("customFields.placeholders.min")}
                value={form.values.min ?? ""}
                onChange={(event) => {
                  if (event.currentTarget.value === "") {
                    form.setFieldValue("min", null)
                  } else {
                    form.getInputProps("min").onChange(event)
                  }
                }}
              />
              <TextInput
                label={
                  <LabelWithTooltip
                    label={t("customFields.labels.max")}
                    hoverHelperText={t("customFields.tooltips.max")}
                    justify="space-between"
                  />
                }
                placeholder={t("customFields.placeholders.max")}
                value={form.values.max ?? ""}
                onChange={(event) => {
                  if (event.currentTarget.value === "") {
                    form.setFieldValue("max", null)
                  } else {
                    form.getInputProps("max").onChange(event)
                  }
                }}
              />
            </Group>
          )}
          <Select
            searchable
            data={activities.map((activity) => ({
              value: activity.id,
              label: activity.name,
            }))}
            clearable
            label={
              <LabelWithTooltip
                label={t("customFields.labels.onActivity")}
                hoverHelperText={t("customFields.tooltips.onActivity")}
                justify="space-between"
                withAsterisk
              />
            }
            placeholder={t("customFields.placeholders.onActivity")}
            {...form.getInputProps("onActivityId")}
          />
          <AutoCompleteWithQuery<AdminResponses.TextContent, PaginationType>
            label={
              <LabelWithTooltip
                label={t("customFields.labels.message")}
                hoverHelperText={t("customFields.tooltips.message")}
                justify="space-between"
              />
            }
            placeholder={t("customFields.placeholders.message")}
            query={useListTextContentsQuery}
            parseOptionFunction={(data) =>
              data?.map((item) => ({
                label: item.originalText,
                value: item.originalText,
              })) || []
            }
            isLoading={isLoadingTextContent}
            onCreate={(query) => {
              updateTextContent({ originalText: query })
              form.setValues({
                ...form.values,
                message: {
                  originalText: query,
                  TextTranslation: [],
                },
                csvColumnName: slugify(query, { lower: true }),
              })
              return null
            }}
            onChange={(value) => {
              if (value) {
                form.setValues({
                  ...form.values,
                  message: {
                    originalText: value,
                    TextTranslation: [],
                  },
                  csvColumnName: slugify(value, { lower: true }),
                })
              } else {
                form.setValues({
                  ...form.values,
                  message: undefined,
                  csvColumnName: undefined,
                })
              }
            }}
            value={form.values.message?.originalText ?? null}
            error={
              errors?.fieldErrors?.message
                ? errors.fieldErrors.message[0]
                : undefined
            }
          />

          <Select
            isLoading={isLoadingEcobalyseFields}
            data={
              ecobalyseFields?.map((ecobalyseBalyseField) => ({
                value: ecobalyseBalyseField.id.toString(),
                label: ecobalyseBalyseField.fieldKey,
              })) ?? []
            }
            label={
              <LabelWithTooltip
                label={t("customFields.labels.ecobalyseField")}
                hoverHelperText={t("customFields.tooltips.ecobalyseField")}
                justify="space-between"
              />
            }
            placeholder={t("customFields.placeholders.ecobalyseField")}
            value={form.values.ecobalyseBalyseFieldId?.toString() ?? null}
            onChange={(value) => {
              form.setFieldValue("ecobalyseBalyseFieldId", value)
            }}
          />
          <Select
            data={Object.values(CustomFieldDocumentRequirement).map(
              (value) => ({
                value,
                label: value,
              })
            )}
            label={
              <LabelWithTooltip
                label={t("customFields.labels.documentRequirement")}
                hoverHelperText={t("customFields.tooltips.documentRequirement")}
                justify="space-between"
              />
            }
            placeholder={t("customFields.placeholders.documentRequirement")}
            {...form.getInputProps("documentRequirement")}
          />
          <Select
            data={Object.values(CustomFieldValueRequirement).map((value) => ({
              value,
              label: value,
            }))}
            label={
              <LabelWithTooltip
                label={t("customFields.labels.fieldRequirement")}
                hoverHelperText={t("customFields.tooltips.fieldRequirement")}
                justify="space-between"
              />
            }
            placeholder={t("customFields.placeholders.fieldRequirement")}
            {...form.getInputProps("fieldRequirement")}
          />
          <Button disabled={!form.isValid()} type="submit" loading={isLoading}>
            {t("customFields.submit")}
          </Button>
        </Stack>
      </form>
    </>
  )
}

type TextContentItemProps = ComboboxItem

export const TextContentSelectItem = forwardRef<
  HTMLDivElement,
  TextContentItemProps
>(({ ...rest }: TextContentItemProps, ref) => (
  <Stack ref={ref} p="xs" gap={0} {...rest}>
    <Text fz="sm">{rest.label}</Text>
  </Stack>
))

export const customFieldConstaintTranslations = (
  constriant: FieldConstraint,
  t: TFunction<"admin", undefined>
) => {
  switch (constriant) {
    case "STRING":
      return t("customFields.constraints.string")
    case "SELECT":
      return t("customFields.constraints.enum")
    case "NUMBER":
      return t("customFields.constraints.number")
    case "MULTI_SELECT":
      return t("customFields.constraints.multi_enum")
    default:
      return constriant
  }
}

const areCustomFieldEcobalyseFieldValuesRequired = (
  ecobalyseFieldId: string | null,
  ecobalyseFields: GetEcobalyseFieldsResponseDto | undefined
) => {
  if (ecobalyseFieldId === null || !ecobalyseFields) {
    return false
  }

  const ecobalyseField = ecobalyseFields.find(
    (field) => field.id === ecobalyseFieldId
  )

  if (!ecobalyseField) {
    return false
  }
  return ecobalyseField?.fieldValues.length > 0
}
